import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { cn, Input, Spinner } from "@porchpass/ui";
import { getOptionalConditions } from "src/api/preApproval";
import type { PreApprovalCondition } from "src/api/preApproval/types";
import { ConditionCardContent } from "./ConditionCardContent";

interface OptionalConditionPickerProps {
  selectedId: string | null;
  onSelect: (condition: PreApprovalCondition) => void;
}

export function OptionalConditionPicker({
  selectedId,
  onSelect,
}: OptionalConditionPickerProps) {
  const { applicationId } = useParams<{ applicationId: string }>();
  const [options, setOptions] = useState<PreApprovalCondition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!applicationId) return;
    setIsLoading(true);
    void getOptionalConditions(applicationId)
      .then(setOptions)
      .finally(() => setIsLoading(false));
  }, [applicationId]);

  const filtered = options.filter((c) =>
    c.title.toLowerCase().includes(search.toLowerCase()),
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner size="s" />
      </div>
    );
  }

  if (options.length === 0) {
    return (
      <p className="text-sm text-[#697083] py-4 text-center">
        No optional conditions available for this application.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Input
        label="Search optional conditions"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="flex flex-col gap-3 max-h-64 overflow-y-auto">
        {filtered.map((condition) => {
          const isSelected = selectedId === condition.id;
          return (
            <button
              key={condition.id}
              type="button"
              onClick={() => onSelect(condition)}
              className={cn(
                "rounded-xl border bg-white p-3.5 flex items-start justify-between gap-4 w-full text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1453F5]/30",
                isSelected
                  ? "border-[#1453F5]"
                  : "border-[#E4E7EC] hover:border-[#9AA2B2]",
              )}
            >
              <ConditionCardContent
                title={condition.title}
                internal_description={condition.internal_description}
                external_description={condition.external_description}
              />
            </button>
          );
        })}
        {filtered.length === 0 && (
          <p className="text-sm text-[#697083] text-center py-2">
            No matches found.
          </p>
        )}
      </div>
    </div>
  );
}
